import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Clock,
  AlertCircle,
  CheckCircle,
  DollarSign,
  TrendingUp,
  MessageSquare,
  RefreshCw,
  ArrowLeft,
  XCircle,
  Star,
  Download,
  Lightbulb,
  Send,
  Trash2,
  Gift,
  X,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_BUSINESS_ID = "6a6cb613-ca2c-4ea2-8cc5-c6c95bb5ecac";
const ADMIN_EMAIL = "esg95@hotmail.com";

const StatCard = ({ icon: Icon, label, value, color, testId, hint }) => (
  <Card className="admin-stat-card" data-testid={testId}>
    <CardContent className="admin-stat-content">
      <div className={`admin-stat-icon ${color}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="admin-stat-text">
        <p className="admin-stat-label">{label}</p>
        <p className="admin-stat-value">{value}</p>
        {hint && <p className="admin-stat-hint">{hint}</p>}
      </div>
    </CardContent>
  </Card>
);

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

const shortDay = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  } catch {
    return iso.slice(5);
  }
};

const statusBadge = (status, isDemo) => {
  if (isDemo) return <Badge className="bg-purple-500">Demo</Badge>;
  if (status === "active") return <Badge className="bg-green-500">Activo</Badge>;
  if (status === "trial") return <Badge className="bg-blue-500">Trial</Badge>;
  if (status === "cancelled") return <Badge className="bg-red-500">Cancelado</Badge>;
  return <Badge variant="outline">{status || "—"}</Badge>;
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [showCourtesy, setShowCourtesy] = useState(false);
  const [courtesyForm, setCourtesyForm] = useState({
    nombre: "",
    email: "",
    password: "",
    plan_id: "monthly",
    duration_months: 1,
    telefono: "",
    idioma: "es",
    notes: "",
  });
  const [creatingCourtesy, setCreatingCourtesy] = useState(false);
  const [subscribers, setSubscribers] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [runningCourtesy, setRunningCourtesy] = useState(false);

  const loadMetrics = async () => {
    try {
      const res = await axios.get(`${API}/admin/metrics?admin_id=${ADMIN_BUSINESS_ID}`);
      setMetrics(res.data);
    } catch (err) {
      toast.error("No se pudieron cargar las métricas");
    } finally {
      setLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setLoadingSubs(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers?admin_id=${ADMIN_BUSINESS_ID}`);
      setSubscribers(res.data);
      setShowSubs(true);
    } catch (err) {
      toast.error("No se pudieron cargar los suscriptores");
    } finally {
      setLoadingSubs(false);
    }
  };

  const handleRunCourtesyCheck = async () => {
    if (!window.confirm("¿Ejecutar la revisión de cortesías ahora?\n\nSe enviarán correos a las cortesías que estén por vencer (3 días antes), que vencieron hoy, o que terminaron su periodo de gracia.")) return;
    setRunningCourtesy(true);
    try {
      const res = await axios.post(`${API}/admin/trigger-courtesy-check?admin_id=${ADMIN_BUSINESS_ID}`);
      const d = res.data;
      toast.success(
        `Revisadas: ${d.checked} · Recordatorios: ${d.reminders_sent} · Expiradas: ${d.expired_sent} · Gracia terminada: ${d.grace_ended}`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar revisión");
    } finally {
      setRunningCourtesy(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/admin/export-clients?admin_id=${ADMIN_BUSINESS_ID}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.download = `suco-clientes-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado correctamente");
    } catch (err) {
      toast.error("Error al exportar CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleSendReport = async () => {
    if (!window.confirm("¿Enviar el reporte semanal ahora a tu correo?")) return;
    setSendingReport(true);
    try {
      const res = await axios.post(
        `${API}/admin/send-weekly-report?admin_id=${ADMIN_BUSINESS_ID}`
      );
      toast.success(`Reporte enviado a ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el reporte");
    } finally {
      setSendingReport(false);
    }
  };

  const handleCleanupTestAccounts = async () => {    setCleaning(true);
    try {
      // Step 1: dry-run preview
      const preview = await axios.post(
        `${API}/admin/cleanup-test-accounts?admin_id=${ADMIN_BUSINESS_ID}&dry_run=true`
      );
      const candidates = preview.data.candidates || [];
      if (candidates.length === 0) {
        toast.success("No hay cuentas de prueba que limpiar");
        return;
      }

      const namesPreview = candidates
        .slice(0, 15)
        .map((c) => `• ${c.nombre || "(sin nombre)"}`)
        .join("\n");
      const extra = candidates.length > 15 ? `\n... y ${candidates.length - 15} más` : "";

      const confirmMsg =
        `Se eliminarán ${candidates.length} cuentas de prueba y todas sus quejas asociadas.\n\n` +
        `${namesPreview}${extra}\n\n` +
        `⚠️ Esta acción NO se puede deshacer. ¿Continuar?`;

      if (!window.confirm(confirmMsg)) return;
      // Second confirmation
      const second = window.prompt(
        `Para confirmar definitivamente, escribe la palabra: BORRAR`
      );
      if (second !== "BORRAR") {
        toast.info("Limpieza cancelada");
        return;
      }

      // Step 2: actually delete
      const res = await axios.post(
        `${API}/admin/cleanup-test-accounts?admin_id=${ADMIN_BUSINESS_ID}&dry_run=false&confirm=true`
      );
      toast.success(`${res.data.deleted_count} cuentas eliminadas`);
      await loadMetrics();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la limpieza");
    } finally {
      setCleaning(false);
    }
  };

  const resetCourtesyForm = () => setCourtesyForm({
    nombre: "", email: "", password: "", plan_id: "monthly",
    duration_months: 1, telefono: "", idioma: "es", notes: "",
  });

  const handleCreateCourtesy = async (e) => {
    e.preventDefault();
    if (!courtesyForm.nombre || !courtesyForm.email || !courtesyForm.password) {
      toast.error("Nombre, email y contraseña son obligatorios");
      return;
    }
    if (courtesyForm.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setCreatingCourtesy(true);
    try {
      const payload = {
        nombre: courtesyForm.nombre,
        email: courtesyForm.email,
        password: courtesyForm.password,
        plan_id: courtesyForm.plan_id,
        idioma: courtesyForm.idioma,
        telefono: courtesyForm.telefono || null,
        notes: courtesyForm.notes || null,
      };
      const dm = parseInt(courtesyForm.duration_months, 10);
      if (!isNaN(dm) && dm > 0) payload.duration_months = dm;
      const res = await axios.post(
        `${API}/admin/businesses/courtesy?admin_id=${ADMIN_BUSINESS_ID}`,
        payload
      );
      toast.success(`Cuenta de cortesía creada para ${res.data.email}`);
      resetCourtesyForm();
      setShowCourtesy(false);
      await loadMetrics();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cuenta");
    } finally {
      setCreatingCourtesy(false);
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem("suco_business");
    if (!stored) {
      navigate("/login");
      return;
    }
    try {
      const biz = JSON.parse(stored);
      const isAdmin = biz.id === ADMIN_BUSINESS_ID || biz.email === ADMIN_EMAIL || biz.emailContacto === ADMIN_EMAIL;
      if (!isAdmin) {
        toast.error("Acceso restringido");
        navigate("/dashboard");
        return;
      }
      setAuthorized(true);
      loadMetrics();
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  if (!authorized) {
    return <div className="loading-container">Verificando acceso...</div>;
  }

  if (loading || !metrics) {
    return <div className="loading-container">Cargando métricas...</div>;
  }

  const c = metrics.counts;
  const cb = metrics.complaints_breakdown || {};
  const chartData = (metrics.registrations_chart || []).map((p) => ({
    ...p,
    label: shortDay(p.date),
  }));

  return (
    <div className="admin-page" data-testid="admin-page">
      <Toaster position="top-center" richColors />
      <header className="admin-header">
        <div className="admin-header-left">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Button>
          <div>
            <h1 className="admin-title">Panel de Administración SUCO</h1>
            <p className="admin-subtitle">Métricas en tiempo real del proceso de venta</p>
          </div>
        </div>
        <div className="admin-header-actions">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSendReport}
            disabled={sendingReport}
            data-testid="admin-send-report-btn"
          >
            <Send className={`h-4 w-4 mr-2 ${sendingReport ? "animate-pulse" : ""}`} />
            {sendingReport ? "Enviando..." : "Enviar reporte ahora"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting}
            data-testid="admin-export-csv-btn"
          >
            <Download className={`h-4 w-4 mr-2 ${exporting ? "animate-spin" : ""}`} />
            {exporting ? "Exportando..." : "Exportar CSV"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadSubscribers}
            disabled={loadingSubs}
            data-testid="admin-subscribers-btn"
          >
            <Star className={`h-4 w-4 mr-2 ${loadingSubs ? "animate-pulse" : ""}`} />
            {loadingSubs ? "Cargando..." : "Ver suscriptores"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCourtesy(true)}
            data-testid="admin-courtesy-btn"
            className="admin-courtesy-btn"
          >
            <Gift className="h-4 w-4 mr-2" />
            Cortesía
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunCourtesyCheck}
            disabled={runningCourtesy}
            data-testid="admin-courtesy-check-btn"
            title="Enviar correos a cortesías por vencer (automático cada día 9 AM)"
          >
            <Clock className={`h-4 w-4 mr-2 ${runningCourtesy ? "animate-spin" : ""}`} />
            {runningCourtesy ? "Revisando..." : "Revisar cortesías"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanupTestAccounts}
            disabled={cleaning}
            data-testid="admin-cleanup-btn"
            className="admin-cleanup-btn"
          >
            <Trash2 className={`h-4 w-4 mr-2 ${cleaning ? "animate-pulse" : ""}`} />
            {cleaning ? "Limpiando..." : "Limpiar pruebas"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadMetrics}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </header>

      <main className="admin-main">
        <section className="admin-grid">
          <StatCard
            icon={Users}
            label="Total de negocios"
            value={c.total}
            color="blue"
            testId="stat-total"
          />
          <StatCard
            icon={Clock}
            label="Trials activos"
            value={c.trial_active}
            color="yellow"
            testId="stat-trial-active"
            hint="Aún en periodo de prueba"
          />
          <StatCard
            icon={AlertCircle}
            label="Trials expirados"
            value={c.trial_expired}
            color="orange"
            testId="stat-trial-expired"
            hint="No convirtieron a pago"
          />
          <StatCard
            icon={CheckCircle}
            label="Suscripciones pagadas"
            value={metrics.paid_subscriptions}
            color="green"
            testId="stat-paid"
          />
          <StatCard
            icon={DollarSign}
            label="MRR estimado"
            value={`$${metrics.mrr_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color="emerald"
            testId="stat-mrr"
            hint="Ingreso recurrente mensual (USD)"
          />
          <StatCard
            icon={TrendingUp}
            label="Tasa de conversión"
            value={`${metrics.conversion_rate_pct}%`}
            color="purple"
            testId="stat-conversion"
            hint="Pagados / total no-demo"
          />
          <StatCard
            icon={XCircle}
            label="Cancelados"
            value={c.cancelled}
            color="red"
            testId="stat-cancelled"
          />
          <StatCard
            icon={MessageSquare}
            label="Quejas/Sugerencias"
            value={metrics.total_complaints}
            color="indigo"
            testId="stat-complaints"
            hint="Total recibidas en la plataforma"
          />
        </section>

        <section className="admin-section-grid">
          <Card className="admin-breakdown-card" data-testid="plan-breakdown">
            <CardHeader>
              <CardTitle className="text-base">Desglose por plan</CardTitle>
              <CardDescription>Suscripciones activas (excluye demos)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="admin-breakdown-list">
                <div className="admin-breakdown-row">
                  <span>Mensual ($12.99)</span>
                  <strong>{c.active_monthly}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span>Anual ($110)</span>
                  <strong>{c.active_yearly}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span>Enterprise Mensual ($29.99+)</span>
                  <strong>{c.active_enterprise_monthly}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span>Enterprise Anual ($299+)</span>
                  <strong>{c.active_enterprise_yearly}</strong>
                </div>
                <div className="admin-breakdown-row admin-breakdown-demo">
                  <span><Star className="h-3 w-3 inline mr-1" /> Cuentas Demo</span>
                  <strong>{c.demo}</strong>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="admin-breakdown-card" data-testid="complaints-breakdown">
            <CardHeader>
              <CardTitle className="text-base">Quejas y Sugerencias</CardTitle>
              <CardDescription>Resumen agregado de toda la plataforma</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="admin-breakdown-list">
                <div className="admin-breakdown-row">
                  <span><AlertCircle className="h-3 w-3 inline mr-1 text-red-500" /> Quejas</span>
                  <strong>{cb.quejas || 0}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span><Lightbulb className="h-3 w-3 inline mr-1 text-yellow-500" /> Sugerencias</span>
                  <strong>{cb.sugerencias || 0}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span><Clock className="h-3 w-3 inline mr-1 text-blue-500" /> Pendientes</span>
                  <strong>{cb.pendientes || 0}</strong>
                </div>
                <div className="admin-breakdown-row">
                  <span><CheckCircle className="h-3 w-3 inline mr-1 text-green-500" /> Resueltas</span>
                  <strong>{cb.resueltas || 0}</strong>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="admin-chart-card" data-testid="registrations-chart-card">
          <CardHeader>
            <CardTitle className="text-base">Registros por día (últimos 30 días)</CardTitle>
            <CardDescription>Evolución del crecimiento de la plataforma</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="admin-chart-wrapper">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={2} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => [`${value} registros`, "Nuevos negocios"]}
                    labelFormatter={(label, items) => {
                      const item = items?.[0]?.payload;
                      return item?.date || label;
                    }}
                  />
                  <Bar dataKey="registros" fill="#4A90D9" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="admin-recent-card" data-testid="recent-registrations">
          <CardHeader>
            <CardTitle className="text-base">Últimos 20 registros</CardTitle>
            <CardDescription>Negocios más recientes</CardDescription>
          </CardHeader>
          <CardContent className="admin-recent-content">
            {metrics.recent_registrations.length === 0 ? (
              <p className="text-muted-foreground">Sin registros aún</p>
            ) : (
              <div className="admin-recent-list">
                {metrics.recent_registrations.map((b) => (
                  <div
                    className="admin-recent-row"
                    key={b.id}
                    data-testid={`recent-${b.id}`}
                  >
                    <div className="admin-recent-info">
                      <p className="admin-recent-name">{b.nombre}</p>
                      <p className="admin-recent-meta">
                        {b.emailContacto || "—"} · {formatDate(b.created_at)}
                        {b.plan_type ? ` · ${b.plan_type}` : ""}
                      </p>
                    </div>
                    <div className="admin-recent-status">
                      {statusBadge(b.subscription_status, b.is_demo)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="admin-generated-at">
          Generado: {new Date(metrics.generated_at).toLocaleString("es-MX")}
          <br/>
          📬 Recibirás este reporte automáticamente cada <strong>lunes a las 8:00 AM (CDMX)</strong> en {ADMIN_EMAIL}.
        </p>
      </main>

      {showCourtesy && (
        <div className="courtesy-modal-backdrop" onClick={() => !creatingCourtesy && setShowCourtesy(false)} data-testid="courtesy-modal">
          <div className="courtesy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="courtesy-modal-header">
              <h2><Gift className="h-5 w-5" /> Crear cuenta de cortesía</h2>
              <button
                className="courtesy-modal-close"
                onClick={() => setShowCourtesy(false)}
                disabled={creatingCourtesy}
                data-testid="courtesy-close-btn"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCourtesy} className="courtesy-form">
              <div className="courtesy-field">
                <label>Nombre del negocio <span className="req">*</span></label>
                <input
                  type="text"
                  value={courtesyForm.nombre}
                  onChange={(e) => setCourtesyForm({...courtesyForm, nombre: e.target.value})}
                  placeholder="Café La Esquina"
                  required
                  data-testid="courtesy-nombre"
                />
              </div>
              <div className="courtesy-field">
                <label>Email del negocio <span className="req">*</span></label>
                <input
                  type="email"
                  value={courtesyForm.email}
                  onChange={(e) => setCourtesyForm({...courtesyForm, email: e.target.value})}
                  placeholder="contacto@cafeesquina.com"
                  required
                  data-testid="courtesy-email"
                />
              </div>
              <div className="courtesy-field">
                <label>Contraseña que tú asignas <span className="req">*</span></label>
                <input
                  type="text"
                  value={courtesyForm.password}
                  onChange={(e) => setCourtesyForm({...courtesyForm, password: e.target.value})}
                  placeholder="mínimo 6 caracteres"
                  minLength={6}
                  required
                  data-testid="courtesy-password"
                />
              </div>
              <div className="courtesy-row">
                <div className="courtesy-field">
                  <label>Plan</label>
                  <select
                    value={courtesyForm.plan_id}
                    onChange={(e) => setCourtesyForm({...courtesyForm, plan_id: e.target.value})}
                    data-testid="courtesy-plan"
                  >
                    <option value="monthly">Mensual ($12.99 equiv.)</option>
                    <option value="yearly">Anual ($110 equiv.)</option>
                    <option value="enterprise_monthly">Enterprise Mensual ($29.99 equiv.)</option>
                    <option value="enterprise_yearly">Enterprise Anual ($299 equiv.)</option>
                  </select>
                </div>
                <div className="courtesy-field">
                  <label>Duración (meses)</label>
                  <select
                    value={courtesyForm.duration_months}
                    onChange={(e) => setCourtesyForm({...courtesyForm, duration_months: e.target.value})}
                    data-testid="courtesy-duration"
                  >
                    <option value="1">1 mes</option>
                    <option value="2">2 meses</option>
                    <option value="3">3 meses</option>
                    <option value="6">6 meses</option>
                    <option value="12">12 meses</option>
                    <option value="">Indefinida</option>
                  </select>
                </div>
              </div>
              <div className="courtesy-row">
                <div className="courtesy-field">
                  <label>Teléfono (opcional)</label>
                  <input
                    type="text"
                    value={courtesyForm.telefono}
                    onChange={(e) => setCourtesyForm({...courtesyForm, telefono: e.target.value})}
                    placeholder="+52 ..."
                    data-testid="courtesy-telefono"
                  />
                </div>
                <div className="courtesy-field">
                  <label>Idioma del negocio</label>
                  <select
                    value={courtesyForm.idioma}
                    onChange={(e) => setCourtesyForm({...courtesyForm, idioma: e.target.value})}
                    data-testid="courtesy-idioma"
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                    <option value="pt">Português</option>
                    <option value="fr">Français</option>
                    <option value="it">Italiano</option>
                    <option value="de">Deutsch</option>
                    <option value="ja">日本語</option>
                    <option value="zh">中文</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>
              </div>
              <div className="courtesy-field">
                <label>Notas internas (opcional)</label>
                <input
                  type="text"
                  value={courtesyForm.notes}
                  onChange={(e) => setCourtesyForm({...courtesyForm, notes: e.target.value})}
                  placeholder="Ej. Promoción Tijuana junio"
                  data-testid="courtesy-notes"
                />
              </div>
              <div className="courtesy-info">
                ℹ️ Se enviará un email de bienvenida al negocio con sus credenciales y el cartel PDF.
              </div>
              <div className="courtesy-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCourtesy(false)}
                  disabled={creatingCourtesy}
                  data-testid="courtesy-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={creatingCourtesy}
                  className="courtesy-submit"
                  data-testid="courtesy-submit-btn"
                >
                  <Gift className={`h-4 w-4 mr-2 ${creatingCourtesy ? "animate-pulse" : ""}`} />
                  {creatingCourtesy ? "Creando..." : "Crear cuenta"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubs && subscribers && (
        <div className="courtesy-modal-backdrop" onClick={() => setShowSubs(false)}>
          <div className="courtesy-modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: '900px'}} data-testid="subscribers-modal">
            <div className="courtesy-modal-header">
              <h2>Suscriptores activos</h2>
              <button className="courtesy-close" onClick={() => setShowSubs(false)} data-testid="subscribers-close-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div style={{padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: '16px'}}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px'}}>
                <div style={{background: '#f0fdf4', padding: '14px', borderRadius: '10px', border: '1px solid #bbf7d0'}}>
                  <div style={{fontSize: '12px', color: '#166534', fontWeight: 600}}>SUSCRIPTORES PAGADOS</div>
                  <div style={{fontSize: '28px', fontWeight: 700, color: '#15803d'}} data-testid="subs-paid-count">{subscribers.paid_count}</div>
                  {subscribers.gateway_counts && (
                    <div style={{fontSize: '11px', color: '#166534', marginTop: '4px'}}>
                      Stripe: {subscribers.gateway_counts.stripe || 0} · PayPal: {subscribers.gateway_counts.paypal || 0}
                      {(subscribers.gateway_counts.desconocido || 0) > 0 && ` · ?: ${subscribers.gateway_counts.desconocido}`}
                    </div>
                  )}
                </div>
                <div style={{background: '#fef3c7', padding: '14px', borderRadius: '10px', border: '1px solid #fde68a'}}>
                  <div style={{fontSize: '12px', color: '#92400e', fontWeight: 600}}>CORTESÍAS ACTIVAS</div>
                  <div style={{fontSize: '28px', fontWeight: 700, color: '#b45309'}} data-testid="subs-courtesy-count">{subscribers.courtesy_count}</div>
                </div>
                <div style={{background: '#eff6ff', padding: '14px', borderRadius: '10px', border: '1px solid #bfdbfe'}}>
                  <div style={{fontSize: '12px', color: '#1e40af', fontWeight: 600}}>MRR ESTIMADO</div>
                  <div style={{fontSize: '28px', fontWeight: 700, color: '#1d4ed8'}} data-testid="subs-mrr">${subscribers.mrr_usd.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                </div>
              </div>

              <div>
                <h3 style={{fontSize: '15px', fontWeight: 600, marginBottom: '8px', color: '#111'}}>Pagados ({subscribers.paid_count})</h3>
                {subscribers.paid.length === 0 ? (
                  <div style={{padding: '20px', background: '#f9fafb', borderRadius: '8px', color: '#6b7280', textAlign: 'center', fontSize: '14px'}}>
                    Aún no hay suscriptores de pago.
                  </div>
                ) : (
                  <div style={{maxHeight: '280px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px'}}>
                    <table style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
                      <thead style={{background: '#f9fafb', position: 'sticky', top: 0}}>
                        <tr>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Negocio</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Email</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Plan</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Pasarela</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Desde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.paid.map((s) => {
                          const gw = s.gateway || 'desconocido';
                          const gwStyle = gw === 'stripe'
                            ? {background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe'}
                            : gw === 'paypal'
                            ? {background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa'}
                            : {background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb'};
                          const gwLabel = gw === 'stripe' ? 'Stripe' : gw === 'paypal' ? 'PayPal' : 'Desconocido';
                          return (
                            <tr key={s.id} style={{borderTop: '1px solid #f3f4f6'}} data-testid={`sub-row-${s.id}`}>
                              <td style={{padding: '10px', fontWeight: 500}}>{s.nombre || '-'}</td>
                              <td style={{padding: '10px', color: '#6b7280'}}>{s.email || s.emailContacto || '-'}</td>
                              <td style={{padding: '10px'}}>
                                <Badge variant="outline">{s.plan_id || s.plan_type || '-'}</Badge>
                              </td>
                              <td style={{padding: '10px'}}>
                                <span style={{...gwStyle, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600}} data-testid={`sub-gateway-${s.id}`}>
                                  {gwLabel}
                                </span>
                              </td>
                              <td style={{padding: '10px', color: '#6b7280'}}>{formatDate(s.subscription_started_at || s.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {subscribers.courtesy.length > 0 && (
                <div>
                  <h3 style={{fontSize: '15px', fontWeight: 600, marginBottom: '8px', color: '#111'}}>Cortesías ({subscribers.courtesy_count})</h3>
                  <div style={{maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px'}}>
                    <table style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
                      <thead style={{background: '#f9fafb', position: 'sticky', top: 0}}>
                        <tr>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Negocio</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Email</th>
                          <th style={{textAlign: 'left', padding: '10px', fontWeight: 600, color: '#374151'}}>Desde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.courtesy.map((s) => (
                          <tr key={s.id} style={{borderTop: '1px solid #f3f4f6'}} data-testid={`courtesy-row-${s.id}`}>
                            <td style={{padding: '10px', fontWeight: 500}}>{s.nombre || '-'}</td>
                            <td style={{padding: '10px', color: '#6b7280'}}>{s.email || s.emailContacto || '-'}</td>
                            <td style={{padding: '10px', color: '#6b7280'}}>{formatDate(s.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px'}}>
                <Button variant="outline" onClick={loadSubscribers} data-testid="subs-refresh-btn">
                  <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
                </Button>
                <Button variant="outline" onClick={() => setShowSubs(false)} data-testid="subs-close-btn">
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
